import {
  CreateOrUpdateQuestionInput,
  Question_Type,
  QuestionQuery,
  SkillBasicQuery,
  useCreateOrUpdateQuestionMutation,
} from "@generated/graphql";
import { Box, Card, Stack } from "@mui/material";
import SaveEditButtons from "components/buttons/SaveEditButtons";
import AutocompleteControl from "components/form/AutocompleteControl";
import SelectControl from "components/form/SelectControl";
import TextFieldControl from "components/form/TextFieldControl";
import QuestionSubtypeSelect from "components/QuestionSubtypeSelect";
import { GRADE_OPTIONS, QUESTION_TYPES } from "constants/global";
import { QUESTION_LANGUAGE_OPTIONS } from "constants/language";
import { Dispatch, SetStateAction, useContext } from "react";
import { useFormContext } from "react-hook-form";
import { cleanSpaceInString } from "helpers/validation/createValidation";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { parseApolloError } from "helpers/errorsParser";
import { useNavigate } from "react-router-dom";
import ROUTES from "constants/routes";
import typography from "constants/typography.constants";
import { Cached } from "@mui/icons-material";
import useIsCreate from "hooks/useIsCreate";
import { QuestionInputs } from "../types";
import GenerateButton from "./GenerateButton";
import useRegenerateQuestion from "../hooks/useRegenerateQuestion";

interface InfoProps {
  question: QuestionQuery["question"] | undefined;
  skill: SkillBasicQuery["skill"] | undefined;
  isEditing: boolean;
  setIsEditing: Dispatch<SetStateAction<boolean>>;
}

const Info = ({ question, skill, isEditing, setIsEditing }: InfoProps) => {
  const isCreate = useIsCreate();
  const form = useFormContext<QuestionInputs>();
  const { handleSubmit, watch } = form;
  const navigate = useNavigate();
  const { addToast } = useContext(ToastContext);

  const questionType = watch("type");
  const isMultiChoiceQuestion = questionType?.id === Question_Type.MultipleChoiceQuestions;

  const [handleRegenerateQuestion, { loading: generationLoading }] = useRegenerateQuestion({
    successText: "Вопрос сгенерирован",
    skillId: skill?.id,
  });

  const [createOrUpdateQuestion, { loading }] = useCreateOrUpdateQuestionMutation();
  const onSubmit = (formData: QuestionInputs) => {
    const timeValue = parseInt(formData.time, 10);

    const input: CreateOrUpdateQuestionInput = {
      criterion: formData.criterion.map((c) => cleanSpaceInString(c.value)),
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      grade: formData.grade?.id!,
      id: question?.id,
      text: formData.text,
      time: timeValue * 60,
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      skillId: skill?.id!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      type: formData.type?.id!,
      subtype: formData.subtype,
      language: formData.language,
      referenceAnswer: formData.referenceAnswer,
    };

    if (isMultiChoiceQuestion) {
      if (!formData.partsInput || formData.partsInput.length < 2) {
        return addToast({ type: ToastTypeEnum.ERROR, text: "Должно быть добавлено хотя бы 2 варианта ответа" });
      }

      input.partsInput = formData.partsInput;
    }

    return createOrUpdateQuestion({
      variables: {
        input,
      },
    })
      .then(({ data }) => {
        const questionId = data?.createOrUpdateQuestion.id;
        if (questionId) {
          setIsEditing(false);
          addToast({ type: ToastTypeEnum.SUCCESS });

          if (isCreate) navigate(`/${ROUTES.QUESTION}/view/${questionId}`, { replace: true });
        } else {
          addToast({ text: typography.errors.pleaseRepeat, type: "error" });
        }
      })
      .catch((e) => parseApolloError(e, { addToast }));
  };

  return (
    <Box sx={{ position: "relative" }}>
      <GenerateButton
        disabled={!isEditing}
        loading={generationLoading}
        onClick={handleRegenerateQuestion}
        variant='contained'
        customEndIcon={<Cached sx={{ width: 24, height: 24 }} />}
        color='white'
        text={isCreate ? "Cобрать вопрос" : "Пересобрать вопрос"}
        sx={{ position: "absolute", transform: "translateY(calc(-100% - 20px))", width: "100%" }}
      />
      <Card>
        <Stack spacing={2}>
          <TextFieldControl
            name='knowledgeBlock.title'
            disabled
            label='Блок знаний'
          />
          <TextFieldControl
            name='title'
            disabled
            label='Навык'
          />
          <AutocompleteControl
            options={GRADE_OPTIONS}
            name='grade'
            label='Грейд'
            disabled={!isEditing}
            rules={{
              required: true,
            }}
          />
          <SelectControl
            name='language'
            items={QUESTION_LANGUAGE_OPTIONS}
            sx={{ backgroundColor: "bgSwitch" }}
            label='Язык вопроса'
            rules={{
              required: true,
            }}
            disabled={!isEditing}
          />
          <AutocompleteControl
            name='type'
            options={QUESTION_TYPES}
            sx={{ backgroundColor: "bgSwitch" }}
            label='Тип вопроса'
            rules={{
              required: true,
            }}
            disabled={!isEditing}
          />
          {(questionType?.id === Question_Type.Practical ||
            questionType?.id === Question_Type.MultipleChoiceQuestions) && (
            <QuestionSubtypeSelect
              questionType={questionType.id}
              questionTypeFieldName='type'
              questionSubtypeFieldName='subtype'
              disabled={!isEditing}
            />
          )}
          <SaveEditButtons
            isEdited={isEditing}
            onSubmit={handleSubmit(onSubmit)}
            setIsEdited={setIsEditing}
            loading={loading}
          />
        </Stack>
      </Card>
    </Box>
  );
};

export default Info;
